import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Mail, Inbox, MoonStar, X, Loader2 } from "lucide-react";
import { V } from "@/lib/vocab";

export const STEPS = [
  { id: "who", label: "Who" },
  { id: "how", label: "How" },
  { id: "write", label: "Write" },
  { id: "review", label: "Review" },
];

export const stepMotion = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
  transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
};

// Numbered progress header — completed steps are clickable so you can hop back.
export const StepHeader = ({ idx, onJump, valid }) => (
  <ol data-testid="wizard-steps" className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1">
    {STEPS.map((s, i) => {
      const done = i < idx, active = i === idx;
      return (
        <li key={s.id} className="flex items-center gap-1 sm:gap-2 shrink-0">
          <button type="button" data-testid={`wizard-step-${s.id}`} onClick={() => done && onJump(i)} disabled={!done}
            aria-current={active ? "step" : undefined}
            className={`inline-flex items-center gap-1.5 rounded-full pl-1 py-1 text-xs font-bold transition-colors ${active ? "pr-3 bg-ink text-white" : "pr-1 sm:pr-3"} ${!active && done ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : !active ? "bg-sand-100 text-ink/40" : ""}`}>
            <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[11px] ${active ? "bg-white/20" : done ? "bg-emerald-600 text-white" : "bg-white text-ink/40"}`}>
              {done ? <Check className="h-3 w-3" strokeWidth={3} /> : i + 1}
            </span>
            <span className={active ? "" : "hidden sm:inline"}>{s.label}</span>
          </button>
          {i < STEPS.length - 1 && <span className={`h-px w-3 sm:w-6 ${done ? "bg-emerald-300" : "bg-sand-200"}`} />}
        </li>
      );
    })}
  </ol>
);

export const StepBody = ({ idx, children }) => (
  <AnimatePresence mode="wait">
    <motion.div key={idx} {...stepMotion}>{children}</motion.div>
  </AnimatePresence>
);

// Sticky on phones so Back / Next never scroll out of reach.
export const WizardFooter = ({ idx, canNext, blocker, onBack, onNext, onSubmit, submitLabel, submitting, danger, extra }) => {
  const last = idx === STEPS.length - 1;
  return (
    <div data-testid="wizard-footer" className="sticky bottom-0 -mx-5 -mb-5 mt-6 px-5 py-3 bg-white/95 backdrop-blur border-t border-sand-100 rounded-b-2xl flex items-center justify-between gap-3">
      <button type="button" data-testid="wizard-back" onClick={onBack} disabled={idx === 0}
        className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-semibold text-ink/70 hover:border-ink hover:text-ink disabled:opacity-40">
        <ChevronLeft className="h-4 w-4" /> Back
      </button>
      <div className="flex items-center gap-3 min-w-0">
        {!canNext && blocker && <span data-testid="wizard-blocker" className="hidden sm:block text-xs text-ink/50 truncate">{blocker}</span>}
        {extra}
        {last ? (
          <button type="button" data-testid="broadcast-send-btn" onClick={onSubmit} disabled={!canNext || submitting}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50 ${danger ? "bg-danger-600 hover:bg-danger-700" : "bg-ink hover:bg-ink-700"}`}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />} {submitLabel}
          </button>
        ) : (
          <button type="button" data-testid="wizard-next" onClick={onNext} disabled={!canNext}
            className="inline-flex items-center gap-1 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-semibold hover:bg-ink-700 disabled:opacity-50">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
      {!canNext && blocker && <span className="sm:hidden absolute -top-7 left-0 right-0 text-center text-[11px] text-ink/55 bg-white/90 py-1">{blocker}</span>}
    </div>
  );
};

const INTRO_KEY = "askredline.messages.intro.v1";
const cardsFactory = () => [
  { icon: Mail, title: "Email first", text: `Email is free; a text costs about a cent a segment per ${V.lc("person")}. Everyone with an email gets the email — only ${V.lcs("person")} without one get a text. Switch to Text first when it's time-sensitive.` },
  { icon: Inbox, title: "Replies land in the Inbox", text: `A text to one ${V.lc("person")} reads like a note from the office, with no STOP footer. Their reply threads into the same conversation, and the concierge handles follow-ups.` },
  { icon: MoonStar, title: "Quiet hours", text: `Texts never go out between 9 PM and 8 AM local time. Schedule one for 11 PM and it moves to 8 AM on its own. Safety alerts are the exception.` },
];

// First-run explainer. Dismiss once and it never comes back.
export const MessagesIntro = () => {
  const CARDS = cardsFactory();
  const [open, setOpen] = useState(() => { try { return !localStorage.getItem(INTRO_KEY); } catch { return true; } });
  if (!open) return null;
  const dismiss = () => { try { localStorage.setItem(INTRO_KEY, "1"); } catch { /* private mode */ } setOpen(false); };
  return (
    <div data-testid="messages-intro" className="relative rounded-2xl border border-sand-200 bg-sand-50/70 p-5 mb-6">
      <button type="button" data-testid="messages-intro-dismiss" onClick={dismiss} aria-label="Dismiss" className="absolute right-3 top-3 rounded-full p-1.5 text-ink/40 hover:bg-white hover:text-ink"><X className="h-4 w-4" /></button>
      <div className="text-[11px] font-black uppercase tracking-wider text-ink/40 mb-3">How Messages works</div>
      <div className="grid gap-3 md:grid-cols-3">
        {CARDS.map((c) => (
          <div key={c.title} className="rounded-xl bg-white border border-sand-200 p-3.5">
            <div className="flex items-center gap-2 font-heading font-bold text-sm text-ink"><c.icon className="h-4 w-4 text-redline-500" strokeWidth={1.75} /> {c.title}</div>
            <p className="text-xs text-ink/60 mt-1.5 leading-relaxed">{c.text}</p>
          </div>
        ))}
      </div>
      <button type="button" data-testid="messages-intro-got-it" onClick={dismiss} className="mt-3 text-xs font-bold text-redline-600 hover:text-redline-700">Got it — don't show this again</button>
    </div>
  );
};
